import { Form, Input, Modal, Radio, Select } from "antd";
import { CalendarOutlined } from "@ant-design/icons";
import AvatarSelect from "../AvatarSelect";
import JDatePicker from "../DatePicker";
import ModalTitle from "../ModalTitle";
import styles from "./styles.module.css";

export const ATTENDANCE_REQUEST_TYPES = {
  manual_punch: "تردد دستی",
  paid_leave: "مرخصی استحقاقی",
  unpaid_leave: "مرخصی بدون حقوق",
  sick_leave: "مرخصی استعلاجی",
  hourly_leave: "مرخصی ساعتی",
  mission: "مأموریت",
  overtime: "اضافه‌کاری",
  correction: "اصلاح تردد",
};

/* Day-long leave and a mission can run across days, so those are the only kinds
 * that take a real from/to range. Overtime, a correction and an hourly leave
 * happen on one day between two clock times, and asking them for two full dates
 * let someone file a correction that began on Sunday and ended on Wednesday —
 * which is not a day's attendance at all. مرخصی ساعتی is the clearest case: it
 * is «سه‌شنبه از ۱۰ تا ۱۲», never a span of days. A manual punch is a single
 * moment on a single day. */
const RANGE_TYPES = ["paid_leave", "unpaid_leave", "sick_leave", "mission"];

const DIRECTION_LABELS = { in: "ورود", out: "خروج" };

/**
 * What to call a request in a list. A manual punch has a direction and the type
 * on its own does not say whether it is an arrival or a departure, which is the
 * only thing a reviewer needs to know about it.
 */
export const attendanceRequestLabel = (row) => {
  const label = ATTENDANCE_REQUEST_TYPES[row?.type] || row?.type;
  if (row?.type !== "manual_punch") return label;

  const direction = DIRECTION_LABELS[row?.direction];
  return direction ? `${label} (${direction})` : label;
};

/** A punch is a single moment, so it has no end to show in a "تا" column. */
export const attendanceRequestHasRange = (row) => row?.type !== "manual_punch";

/** The date half of what the picker emits ("YYYY-MM-DDTHH:mm"). */
const datePart = (value) => String(value || "").split("T")[0];

const compose = (date, time) => `${datePart(date)}T${time}`;

export default function AttendanceRequestModal({ open, loading, onClose, onSave, users }) {
  const [form] = Form.useForm();
  const type = Form.useWatch("type", form) || "manual_punch";
  const isRange = RANGE_TYPES.includes(type);
  const isPunch = type === "manual_punch";
  /* An hourly leave and an overtime are a window inside one day, so their two
     clock fields are «از/تا», not the arrival and departure a correction sets. */
  const isPeriod = type === "hourly_leave" || type === "overtime";

  const handleFinish = (values) => {
    /* Only a manager is given the user picker, and only then is user_id sent —
     * for everyone else the request belongs to whoever is signed in. */
    const onBehalf = values.user_id ? { user_id: values.user_id } : {};

    if (RANGE_TYPES.includes(values.type)) {
      onSave({
        ...onBehalf,
        type: values.type,
        starts_at: values.starts_at,
        ends_at: values.ends_at,
        reason: values.reason,
      });
      return;
    }

    /* Both remaining shapes are one day; the API still stores a start and an
       end, so the day is combined with the clock times here rather than asking
       the person for the same date twice. A punch has no span — it is sent as a
       zero-length range with the direction saying which edge it is. */
    if (values.type === "manual_punch") {
      const at = compose(values.work_date, values.punch_time);
      onSave({
        ...onBehalf,
        type: values.type,
        starts_at: at,
        ends_at: at,
        direction: values.direction,
        reason: values.reason,
      });
      return;
    }

    onSave({
      ...onBehalf,
      type: values.type,
      starts_at: compose(values.work_date, values.start_time),
      ends_at: compose(values.work_date, values.end_time),
      reason: values.reason,
    });
  };

  return (
    <Modal
      open={open}
      title={
        <ModalTitle
          icon={<CalendarOutlined />}
          tone="blue"
          title="درخواست جدید"
          subtitle="ثبت تردد، مرخصی، مأموریت، اضافه‌کاری یا اصلاح تردد"
        />
      }
      className="ds-modal"
      centered
      width={560}
      okText="ثبت درخواست"
      cancelText="انصراف"
      confirmLoading={loading}
      onOk={() => form.submit()}
      onCancel={onClose}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        initialValues={{ type: "manual_punch", direction: "in" }}
      >
        {users?.length > 0 && (
          <Form.Item
            name="user_id"
            label="برای کدام همکار؟"
            extra="خالی بگذارید تا برای خودتان ثبت شود. درخواستی که برای همکار ثبت کنید مستقیم تأییدشده ثبت می‌شود."
          >
            <AvatarSelect bordered allowClear showSearch className="w-100" options={users} placeholder="خودم" />
          </Form.Item>
        )}

        <Form.Item
          name="type"
          label="نوع درخواست"
          rules={[{ required: true, message: "نوع درخواست را انتخاب کنید" }]}
        >
          <Select
            options={Object.entries(ATTENDANCE_REQUEST_TYPES).map(
              ([value, label]) => ({ value, label }),
            )}
          />
        </Form.Item>

        {isRange ? (
          <div className={styles.twoCol}>
            <Form.Item
              name="starts_at"
              label="از"
              rules={[{ required: true, message: "زمان شروع را انتخاب کنید" }]}
            >
              <JDatePicker showTime fullWidth />
            </Form.Item>
            <Form.Item
              name="ends_at"
              label="تا"
              rules={[{ required: true, message: "زمان پایان را انتخاب کنید" }]}
            >
              <JDatePicker showTime fullWidth />
            </Form.Item>
          </div>
        ) : (
          <>
            <Form.Item
              name="work_date"
              label="تاریخ"
              rules={[{ required: true, message: "تاریخ را انتخاب کنید" }]}
            >
              <JDatePicker fullWidth />
            </Form.Item>

            {isPunch ? (
              <div className={styles.twoCol}>
                <Form.Item name="direction" label="نوع تردد">
                  <Radio.Group buttonStyle="solid" className={styles.directions}>
                    <Radio.Button value="in">ورود</Radio.Button>
                    <Radio.Button value="out">خروج</Radio.Button>
                  </Radio.Group>
                </Form.Item>
                <Form.Item
                  name="punch_time"
                  label="ساعت"
                  rules={[{ required: true, message: "ساعت را وارد کنید" }]}
                >
                  <Input type="time" />
                </Form.Item>
              </div>
            ) : (
              <div className={styles.twoCol}>
                <Form.Item
                  name="start_time"
                  label={isPeriod ? "از ساعت" : "ساعت ورود"}
                  rules={[{ required: true, message: "ساعت شروع را وارد کنید" }]}
                >
                  <Input type="time" />
                </Form.Item>
                <Form.Item
                  name="end_time"
                  label={isPeriod ? "تا ساعت" : "ساعت خروج"}
                  dependencies={["start_time"]}
                  rules={[
                    { required: true, message: "ساعت پایان را وارد کنید" },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const start = getFieldValue("start_time");
                        if (!value || !start || value > start) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error("ساعت پایان باید بعد از شروع باشد"),
                        );
                      },
                    }),
                  ]}
                >
                  <Input type="time" />
                </Form.Item>
              </div>
            )}
          </>
        )}

        <Form.Item
          name="reason"
          label="توضیحات"
          rules={[{ required: true, message: "علت درخواست را بنویسید" }]}
        >
          <Input.TextArea
            rows={4}
            placeholder="فراموش کردم."
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}
